import { useEffect, useState } from 'react';
import { getAuthHeaders, getImpersonationIdentityKey, makeApiCall } from '../utils/apiConfig';
import { useAuth } from '../contexts/AuthContext';
import { useOrganizations } from '../contexts/OrganizationContext';

/** A product's live trade-in credit, as `GET /store/pricing/breakdown` serves it (MOT-742). */
export interface TradeInCredit {
  amount: number;
  kind: 'trade_in' | 'trade_up_bonus';
  offer: string;
  startsAt: string | null;
  endsAt: string | null;
}

/**
 * The live trade-in credits of a product, batched across every card asking at
 * once (up to 50 per call to `/store/pricing/trade-in-credits`, which reads
 * only the credits, not a whole price breakdown), cached per acting
 * identity (user, View-as target and acting store, so a store switch never
 * shows another store's credits) for `CACHE_TTL_MS`, after which a mounted
 * card asks again so an offer that ended drops off. Saving the store's switch
 * calls `invalidateTradeInCredits()`, which clears the cache and refetches
 * every mounted line. The route serves credits only while the flag
 * `trade_in_credit_display` and the store's own switch are on; otherwise this
 * answers an empty list.
 */
type Listener = (credits: TradeInCredit[]) => void;

const CACHE_TTL_MS = 5 * 60_000;
const pending = new Set<string>();
const listeners = new Map<string, Set<Listener>>();
const cache = new Map<string, { credits: TradeInCredit[]; at: number }>();
const timers = new Map<string, ReturnType<typeof setTimeout>>();
/** Bumped by `invalidateTradeInCredits`; mounted hooks re-request when it moves. */
let generation = 0;
const generationListeners = new Set<(value: number) => void>();

const fresh = (key: string): TradeInCredit[] | undefined => {
  const entry = cache.get(key);
  return entry && Date.now() - entry.at < CACHE_TTL_MS ? entry.credits : undefined;
};

const cacheKey = (identityKey: string, productId: string) => `${identityKey}:${productId}`;

function schedule(identityKey: string) {
  if (timers.has(identityKey)) return;
  timers.set(
    identityKey,
    setTimeout(() => {
      timers.delete(identityKey);
      void flush(identityKey);
    }, 40)
  );
}

async function flush(identityKey: string) {
  const ids = Array.from(pending)
    .filter((key) => key.startsWith(`${identityKey}:`))
    .map((key) => key.slice(identityKey.length + 1))
    .slice(0, 50);
  ids.forEach((id) => pending.delete(cacheKey(identityKey, id)));
  if (ids.length === 0) return;

  const notify = (id: string, credits: TradeInCredit[] | null) => {
    const key = cacheKey(identityKey, id);
    if (credits) cache.set(key, { credits, at: Date.now() });
    listeners.get(key)?.forEach((cb) => cb(credits || []));
    listeners.delete(key);
  };

  try {
    const response = await makeApiCall(`/store/pricing/trade-in-credits?productIds=${encodeURIComponent(ids.join(','))}`, {
      headers: await getAuthHeaders(),
    });
    if (!response.ok) throw new Error('breakdown request failed');
    const body = await response.json();
    const items: Array<{ productId: string; found?: boolean; tradeInCredits?: TradeInCredit[] }> = body?.data?.items || [];
    const byId = new Map(items.map((item) => [item.productId, item]));
    for (const id of ids) notify(id, byId.get(id)?.tradeInCredits || []);
  } catch {
    ids.forEach((id) => notify(id, null));
  }

  if (Array.from(pending).some((key) => key.startsWith(`${identityKey}:`))) schedule(identityKey);
}

/** Empty while unknown, on error, or when nothing is live. */
export function useTradeInCredits(productId: string | undefined, enabled: boolean): TradeInCredit[] {
  const { currentUser } = useAuth();
  const { actingOrganizationId } = useOrganizations();
  const identityKey = `${currentUser?.uid || 'anonymous'}|${getImpersonationIdentityKey()}|${actingOrganizationId || ''}`;
  const key = productId ? cacheKey(identityKey, productId) : undefined;
  const [credits, setCredits] = useState<TradeInCredit[]>(() => (key && fresh(key)) || []);
  const [gen, setGen] = useState(generation);

  useEffect(() => {
    generationListeners.add(setGen);
    return () => {
      generationListeners.delete(setGen);
    };
  }, []);

  useEffect(() => {
    if (!enabled || !productId || !key) {
      setCredits([]);
      return;
    }
    const cached = fresh(key);
    let cb: Listener | null = null;
    if (cached) {
      setCredits(cached);
    } else {
      // The key carries the acting identity, so only this identity's own expired
      // answer is kept while asking again; another store's is never shown.
      setCredits(cache.get(key)?.credits || []);
      cb = (value) => setCredits(value);
      if (!listeners.has(key)) listeners.set(key, new Set());
      listeners.get(key)!.add(cb);
      pending.add(key);
      schedule(identityKey);
    }
    // Ask again once the cached answer would have expired.
    const revalidate = setTimeout(() => setGen((g) => g + 1), CACHE_TTL_MS);
    return () => {
      clearTimeout(revalidate);
      if (cb) listeners.get(key)?.delete(cb);
    };
  }, [enabled, productId, key, identityKey, gen]);

  return credits;
}

/** Drop every cached answer and refetch every mounted line: called when the store saves its switch. */
export function invalidateTradeInCredits(): void {
  cache.clear();
  generation += 1;
  generationListeners.forEach((listener) => listener(generation));
}

/** Test seam. */
export function resetTradeInCreditsCache(): void {
  generationListeners.clear();
  pending.clear();
  listeners.clear();
  cache.clear();
  timers.forEach((timer) => clearTimeout(timer));
  timers.clear();
}
